/**
 * Adherence tracking hooks
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api-client';

// ==================== Types ====================

export interface AdherenceDetails {
  patient_id: number;
  period_days: number;
  medication: {
    adherence_percentage: number;
    doses_taken: number;
    doses_scheduled: number;
  };
  exercise: {
    sessions_completed: number;
    adherence_percentage: number;
  };
  home_tests: {
    tests_completed: number;
    adherence_percentage: number;
  };
}

export interface LowAdherencePatient {
  patient_id: number;
  patient_name: string;
  adherence_score: number;
  status: string;
}

export interface LowAdherenceResponse {
  patients: LowAdherencePatient[];
  threshold: number;
  count: number;
}

export interface MedicationAdherenceData {
  overall: number;
  scheduled_doses: number;
  taken_doses: number;
  missed_doses: number;
  medications: {
    id: number;
    name: string;
    dosage: string;
    frequency: string;
    scheduled: number;
    taken: number;
    missed_dates: string[];
  }[];
}

export interface ExerciseAdherenceData {
  overall: number;
  assigned_exercises: number;
  completed_exercises: number;
  exercises: {
    id: number;
    name: string;
    type: string;
    scheduled_per_week: number;
    completed_this_week: number;
    last_completed: string | null;
    missed_dates: string[];
  }[];
}

export interface HomeTestAdherenceData {
  overall: number;
  scheduled_tests: number;
  completed_tests: number;
  tests: {
    id: number;
    test_name: string;
    frequency: string;
    last_completed: string | null;
    next_scheduled: string;
    missed_count: number;
    missed_dates: string[];
  }[];
}

export interface ComprehensiveAdherenceDetails {
  patient_id: number;
  patient_name: string;
  age: number;
  cohort: string;
  overall_adherence: number;
  period_days: number;
  medication: MedicationAdherenceData;
  exercise: ExerciseAdherenceData;
  home_test: HomeTestAdherenceData;
}

// ==================== API Functions ====================

/**
 * Get low adherence patients
 */
async function fetchLowAdherencePatients(
  threshold: number
): Promise<LowAdherenceResponse> {
  const response = await apiClient.get(`/adherence/low?threshold=${threshold}`);
  return response;
}

/**
 * Helper function to fetch adherence details for a specific patient
 */
async function fetchAdherenceDetails(
  patientId: number,
  days: number = 7
): Promise<AdherenceDetails> {
  const response = await apiClient.get(`/adherence/${patientId}/details?days=${days}`);
  return response;
}

/**
 * Helper function to fetch comprehensive adherence details for a specific patient
 */
async function fetchComprehensiveAdherenceDetails(
  patientId: number,
  days: number = 30
): Promise<ComprehensiveAdherenceDetails> {
  const response = await apiClient.get(`/adherence/${patientId}/comprehensive?days=${days}`);
  return response;
}

// ==================== React Query Hooks ====================

/**
 * Hook to get low adherence patients
 */
export function useLowAdherencePatients(threshold: number = 75) {
  return useQuery({
    queryKey: ['adherence', 'low', threshold],
    queryFn: () => fetchLowAdherencePatients(threshold),
  });
}

/**
 * Hook to get patient adherence details
 */
export function usePatientAdherenceDetails(patientId: number, days: number = 30) {
  return useQuery({
    queryKey: ['adherence', 'details', patientId, days],
    queryFn: () => fetchAdherenceDetails(patientId, days),
    enabled: !!patientId,
  });
}

/**
 * Hook to get comprehensive patient adherence details
 * Includes detailed medication, exercise, and test lists with missed dates
 */
export function useComprehensiveAdherenceDetails(patientId: number, days: number = 30) {
  return useQuery({
    queryKey: ['adherence', 'comprehensive', patientId, days],
    queryFn: () => fetchComprehensiveAdherenceDetails(patientId, days),
    enabled: !!patientId && patientId > 0,
  });
}